// client/src/components/leads/b2c/CommunicationPanel.js
// FINAL VERSION: Simplified "Email" button that opens email client directly
// Client-side intelligence - no backend dependency

import React, { useState, useEffect } from 'react';

export default function CommunicationPanel({ customer, onSendEmail }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    generateSummary();
  }, [customer]);

  const generateSummary = () => {
    // Client-side intelligence based on customer data
    const visitCount = customer.visitCount || 0;
    const lifetimeSpend = parseFloat(customer.lifetimeSpend || 0);
    const lastVisitDate = customer.lastVisitDate ? new Date(customer.lastVisitDate) : null;
    const daysSinceLastContact = lastVisitDate 
      ? Math.floor((new Date() - lastVisitDate) / (1000 * 60 * 60 * 24))
      : null;

    let status = 'New Customer';
    let riskLevel = 'Low';
    let insights = [];
    let recommendations = [];

    // Determine status
    if (visitCount === 0) {
      status = 'New Customer';
      insights.push('No visits recorded yet - great opportunity to make a strong first impression');
      recommendations.push('Send welcome email with introduction to your services');
      recommendations.push('Offer first-time customer discount to encourage first visit');
    } else if (visitCount >= 10 || lifetimeSpend >= 500) {
      status = 'VIP';
      insights.push(`Loyal customer with ${visitCount} visits and £${lifetimeSpend.toFixed(2)} lifetime value`);
      recommendations.push('Thank them for their loyalty with exclusive perks');
      recommendations.push('Ask for testimonial or referral');
    } else if (daysSinceLastContact && daysSinceLastContact > 60) {
      status = 'At Risk';
      riskLevel = 'High';
      insights.push(`${daysSinceLastContact} days since last contact - immediate action needed`);
      recommendations.push('Send personalized win-back offer');
      recommendations.push('Ask for feedback on their experience');
    } else if (visitCount >= 3) {
      status = 'Regular';
      insights.push('Established customer relationship with regular engagement');
      recommendations.push('Continue regular communication');
      recommendations.push('Encourage referrals and reviews');
    } else {
      status = 'Active';
      insights.push('Building relationship - keep the momentum going');
      recommendations.push('Follow up on their experience');
      recommendations.push('Personalize your communication based on their interests');
    }

    // Risk assessment
    if (daysSinceLastContact > 60) {
      riskLevel = 'High';
    } else if (daysSinceLastContact > 30) {
      riskLevel = 'Medium';
    }

    setSummary({
      status,
      riskLevel,
      insights,
      recommendations,
      visitCount,
      lifetimeSpend
    });
  };

  const getRiskBadge = (riskLevel) => {
    const colors = {
      'High': 'bg-red-100 text-red-700 border-red-200',
      'Medium': 'bg-orange-100 text-orange-700 border-orange-200',
      'Low': 'bg-green-100 text-green-700 border-green-200'
    };
    
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${colors[riskLevel] || colors.Low}`}>
        {riskLevel} Risk
      </span>
    );
  };

  if (!summary) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Communication</h3>
        <span className="text-xl">💬</span>
      </div>

      {/* Customer Status */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-medium text-gray-700">Status:</span>
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
            {summary.status}
          </span>
          {getRiskBadge(summary.riskLevel)}
        </div>
      </div>

      {/* AI Insights */}
      {summary.insights && summary.insights.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">💡 Insights</h4>
          <div className="space-y-2">
            {summary.insights.map((insight, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-sm text-blue-900">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {summary.recommendations && summary.recommendations.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">🎯 Recommended Actions</h4>
          <div className="space-y-2">
            {summary.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <p className="text-sm text-gray-700 flex-1">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions - CHANGED: Single "Email" button */}
      <div className="space-y-3">
        <button
          onClick={() => onSendEmail && onSendEmail(customer)}
          className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border border-green-600"
        >
          <span>✉️</span>
          <span>Email</span>
        </button>
        
        {customer.phone && (
          <button
            onClick={() => window.location.href = `tel:${customer.phone}`}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <span>📞</span>
            <span>Call Customer</span>
          </button>
        )}
      </div>

      {/* Contact Info Status */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Email</span>
            <span className={customer.email ? 'text-green-600' : 'text-gray-400'}>
              {customer.email ? '✓ Available' : '✗ Missing'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Phone</span>
            <span className={customer.phone ? 'text-green-600' : 'text-gray-400'}>
              {customer.phone ? '✓ Available' : '✗ Missing'}
            </span>
          </div>
        </div>
        
        {(!customer.email || !customer.phone) && (
          <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-700">
              💡 Add missing contact info to enable full communication options
            </p>
          </div>
        )}
      </div>

    </div>
  );
}